/**
 * DTC (Diagnostic Trouble Code) decoding — SAE J2012 bit layout.
 *
 * A DTC is two bytes on the wire. The byte-to-string algorithm:
 *   byte A bits 7-6  → system letter   00=P 01=C 10=B 11=U
 *   byte A bits 5-4  → first digit     0..3
 *   byte A bits 3-0  → second hex digit
 *   byte B           → third+fourth hex digits
 *
 * e.g. (0x01, 0x33) → P0133 ; (0xC1, 0x58) → U0158.
 */
const SYSTEM_LETTERS = ['P', 'C', 'B', 'U'];
/** Decode a 2-byte DTC into its canonical 5-character code string. */
export function decodeDtcBytes(a, b) {
    const system = SYSTEM_LETTERS[(a >> 6) & 0b11];
    const firstDigit = (a >> 4) & 0b11; // 0..3
    const secondDigit = (a & 0x0f).toString(16).toUpperCase(); // 0..F
    const lastTwo = (b & 0xff).toString(16).toUpperCase().padStart(2, '0'); // 00..FF
    return `${system}${firstDigit}${secondDigit}${lastTwo}`;
}
/**
 * Decode a Mode 03 (stored) or Mode 07 (pending) response into DTCs.
 *
 * Input is the cleaned byte array from `cleanElmResponse`. The first byte is
 * the positive-response service id (0x43 for Mode 03, 0x47 for Mode 07) and is
 * consumed, never paired — this avoids the classic bug where a 0x43 prefix is
 * mistaken for the first byte of a C03xx code. Remaining bytes are paired;
 * `0x0000` padding pairs are dropped.
 *
 * Framing assumption: CAN (ISO 15765-4), where the Service 03/07 response is
 * the service byte followed directly by DTC pairs with NO count byte. Legacy
 * ISO 9141 / KWP2000 protocols prepend a count byte; not handled in v1 because
 * target vehicles (2016+, incl. the Sonata Hybrid) all use CAN.
 */
export function decodeDtcResponse(bytes, status) {
    const serviceByte = status === 'stored' ? 0x43 : 0x47;
    if (bytes.length === 0 || bytes[0] !== serviceByte)
        return [];
    const data = bytes.slice(1);
    const seen = new Set();
    const dtcs = [];
    for (let i = 0; i + 1 < data.length; i += 2) {
        const a = data[i];
        const b = data[i + 1];
        if (a === undefined || b === undefined)
            break;
        if (a === 0 && b === 0)
            continue; // ISO-TP padding
        const code = decodeDtcBytes(a, b);
        if (seen.has(code))
            continue;
        seen.add(code);
        dtcs.push({ code, system: code[0], status });
    }
    return dtcs;
}
//# sourceMappingURL=dtc.js.map